/**
 * Remembers the rep's last dialer choices so reopening the dialer lands on the
 * setup they were already using, instead of resetting to the default channel
 * every time.
 *
 * Local only, deliberately. The caller ID that VoBiz actually dials with comes
 * from the server (member_vobiz.fromNumber), and selecting a number already
 * writes it there — so these values are a fast pre-select, never the source of
 * truth. If they ever disagree with the server, the server wins.
 *
 * Scoped per member: shared machines must not leak one rep's number to another.
 */

const PREFIX = "sg.dialer.prefs";

const keyFor = memberId => `${PREFIX}.${memberId || "anonymous"}`;

/** localStorage throws in private mode and when the quota is full — never let that break the dialer. */
const readPrefs = memberId => {
  try {
    const raw = window.localStorage.getItem(keyFor(memberId));
    return raw ? JSON.parse(raw) || {} : {};
  } catch (e) {
    return {};
  }
};

const writePrefs = (memberId, prefs) => {
  try {
    window.localStorage.setItem(keyFor(memberId), JSON.stringify(prefs));
  } catch (e) {
    // Preference persistence is a convenience — losing it must never block a call.
  }
};

export const getPreferredChannel = memberId => readPrefs(memberId).channel || "";

export const setPreferredChannel = (memberId, channel) => {
  if (!channel) return;
  writePrefs(memberId, { ...readPrefs(memberId), channel });
};

/** Caller ID is remembered per channel — an India DID is meaningless on the Twilio channel. */
export const getPreferredCallerId = (memberId, channel) => {
  const { callerIds = {} } = readPrefs(memberId);
  return callerIds[channel] || "";
};

export const setPreferredCallerId = (memberId, channel, number) => {
  if (!channel || !number || number === "purchaseNumber") return;
  const prefs = readPrefs(memberId);
  writePrefs(memberId, {
    ...prefs,
    callerIds: { ...(prefs.callerIds || {}), [channel]: number }
  });
};

export const clearDialerPreferences = memberId => {
  try {
    window.localStorage.removeItem(keyFor(memberId));
  } catch (e) {
    // ignore
  }
};
